import React, { useState, useRef, useCallback, memo } from 'react';
import { addressApi } from '../services/api';
import { Mic, MicOff } from 'lucide-react';

const AddressInput = memo(({ value, onSelect, onChange, placeholder, className, testId }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const debounceRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setLocalValue(val);
    if (onChange) onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length >= 3) {
      debounceRef.current = setTimeout(async () => {
        try {
          const response = await addressApi.autocomplete(val);
          setSuggestions(response.data || []);
          setShowSuggestions(true);
        } catch (err) { /* ignore */ }
      }, 400);
    } else {
      setShowSuggestions(false);
    }
  }, [onChange]);

  const handleSelect = useCallback((suggestion) => {
    const desc = suggestion.description || suggestion.label || '';
    setLocalValue(desc);
    setShowSuggestions(false);
    setSuggestions([]);
    if (onChange) onChange(desc);
    if (onSelect) onSelect(suggestion);
  }, [onChange, onSelect]);

  const handleMic = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setLocalValue(transcript);
      if (onChange) onChange(transcript);
      setIsListening(false);
      // Délai 300ms > onBlur 200ms pour ne pas que les suggestions soient cachées
      if (transcript.length >= 3) {
        setTimeout(async () => {
          try {
            const response = await addressApi.autocomplete(transcript);
            const results = response.data || [];
            if (results.length > 0) {
              setSuggestions(results);
              inputRef.current?.focus(); // re-focus pour garder les suggestions visibles
              setShowSuggestions(true);
            }
          } catch (err) { /* ignore */ }
        }, 300);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  }, [isListening, onChange]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder || "Entrez une adresse..."}
        className={className || "w-full p-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none text-sm"}
        data-testid={testId || "address-input"}
      />
      <button
        type="button"
        onClick={handleMic}
        title="Dicter l'adresse"
        data-testid="address-mic-btn"
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all ${
          isListening
            ? 'bg-red-100 text-red-500 animate-pulse'
            : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
        }`}
      >
        {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm transition-colors border-b last:border-0"
              onMouseDown={() => handleSelect(s)}
            >
              {s.description || s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

AddressInput.displayName = 'AddressInput';
export default AddressInput;
